
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/contexts/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getUserInitials } from '@/components/navbar/utils';
import { Upload, Camera, RefreshCw } from 'lucide-react';

const formSchema = z.object({
  username: z.string().min(2, 'Username must be at least 2 characters').max(30, 'Username must be at most 30 characters'),
  displayName: z.string().optional(),
  email: z.string().email('Please enter a valid email').optional(),
});

type ProfileFormValues = z.infer<typeof formSchema>;

const ProfileForm = () => {
  const { user, isGuest } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: user?.user_metadata?.username || '',
      displayName: user?.user_metadata?.display_name || '',
      email: user?.email || '',
    },
  });

  // Fetch profile data
  useEffect(() => {
    const getProfile = async () => {
      if (!user) return;
      
      setIsLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error fetching profile:', error);
        } else if (data) {
          setProfile(data);
          setAvatarUrl(data.avatar_url);
          // Update form with profile data
          form.setValue('username', data.username || user?.user_metadata?.username || '');
          form.setValue('displayName', user?.user_metadata?.display_name || '');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    getProfile();
  }, [user, form]);

  // Upload profile picture
  const uploadProfilePicture = async (file: File) => {
    if (!user || !file) return;
    
    setIsUploading(true);
    
    try {
      // Create a unique file path for the user's avatar
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${fileExt}`;
      
      // Upload the file to storage
      const { error: uploadError } = await supabase.storage
        .from('profile_pictures')
        .upload(filePath, file, { upsert: true });
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('profile_pictures')
        .getPublicUrl(filePath);
      
      // Update the avatar_url in the profiles table
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);
      
      if (updateError) {
        throw updateError;
      }
      
      // Also update in auth.users metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: {
          avatar_url: publicUrl
        }
      });
      
      if (metadataError) {
        throw metadataError;
      }
      
      setAvatarUrl(publicUrl);
      
      toast({
        title: "Profile picture updated",
        description: "Your profile picture has been successfully updated",
      });
    } catch (error: any) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: "Failed to update profile picture",
        description: error.message || "An error occurred while updating your profile picture",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file input change
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    const file = event.target.files[0];
    
    // Check if the file is an image
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (jpeg, png, etc.)",
        variant: "destructive",
      });
      return;
    }
    
    // Check if the file size is less than 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image less than 5MB",
        variant: "destructive",
      });
      return;
    }
    
    uploadProfilePicture(file);
  };

  // Trigger file input click
  const handleUploadClick = () => {
    document.getElementById('profile-picture-upload')?.click();
  };

  const onSubmit = async (data: ProfileFormValues) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          username: data.username,
          display_name: data.displayName,
        }
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Update profile in profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          username: data.username 
        })
        .eq('id', user.id);
      
      if (profileError) {
        throw profileError;
      }
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated",
      });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Failed to update profile",
        description: error.message || "An error occurred while updating your profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6">
        <div className="relative">
          <Avatar className="h-24 w-24">
            <AvatarImage 
              src={avatarUrl || user?.user_metadata?.avatar_url} 
              alt={user?.user_metadata?.username || 'Profile'}
            />
            <AvatarFallback className="bg-purple-DEFAULT text-white text-xl">
              {getUserInitials(user, isGuest)}
            </AvatarFallback>
          </Avatar>
          
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
              <RefreshCw className="h-8 w-8 text-white animate-spin" />
            </div>
          )}
          
          <div className="absolute -bottom-2 -right-2 flex space-x-1">
            <Button 
              type="button" 
              size="sm" 
              variant="secondary" 
              className="rounded-full h-8 w-8 p-0"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <Camera className="h-4 w-4" />
              <span className="sr-only">Upload photo</span>
            </Button>
          </div>
        </div>
        
        <div className="flex-1 space-y-2">
          <h3 className="text-lg font-medium">Profile Picture</h3>
          <p className="text-sm text-muted-foreground">
            This is your public profile picture. It will be visible to other users.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUploadClick}
            className="mt-2"
            disabled={isUploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            Change Picture
          </Button>
        </div>
      </div>
      
      <div className="border-t pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your username" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your display name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your email address"
                      {...field}
                      disabled
                      className="bg-muted"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ProfileForm;
